// Copyright (c) 2022. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import { MY_INVENTORY_DATA_CLASS_NAME } from "../../../../../../core/constants/className";
import { TranslationFunction } from "../../../../../../../../io/hyperify/core/types/TranslationFunction";
import { MyInventoryDataForm } from "./MyInventoryDataForm";
import {
    T_MY_SHELL_INVENTORY_DATA_INCORRECT_DATA,
    T_INVENTORY_ITEM_STATE_PREFIX,
    T_MY_SHELL_INVENTORY_DATA_FIELD_HOSTNAME_LABEL,
    T_MY_SHELL_INVENTORY_DATA_FIELD_PORT_LABEL,
    T_MY_SHELL_INVENTORY_DATA_FIELD_REAL_NAME_LABEL,
    T_MY_SHELL_INVENTORY_DATA_FIELD_USERNAME_LABEL,
    T_MY_SHELL_INVENTORY_DATA_RESET_LABEL,
    T_MY_SHELL_INVENTORY_DATA_STATE_LABEL,
    T_MY_SHELL_INVENTORY_DATA_SAVE_LABEL,
    T_MY_SHELL_INVENTORY_DATA_APPROVE_LABEL,
    T_MY_EMAIL_INVENTORY_DATA_STATE_LABEL,
    T_MY_EMAIL_INVENTORY_DATA_APPROVE_LABEL,
    T_MY_EMAIL_INVENTORY_DATA_SAVE_LABEL,
    T_MY_EMAIL_INVENTORY_DATA_RESET_LABEL,
    T_MY_EMAIL_INVENTORY_DATA_INCORRECT_DATA,
    T_MY_EMAIL_INVENTORY_DATA_FIELD_HOSTNAME_LABEL,
    T_MY_EMAIL_INVENTORY_DATA_FIELD_USERNAME_LABEL,
    T_MY_EMAIL_INVENTORY_DATA_FIELD_USERNAME_PLACEHOLDER,
    T_MY_SHELL_INVENTORY_DATA_FIELD_USERNAME_PLACEHOLDER,
    T_MY_WEB_HOTEL_INVENTORY_DATA_STATE_LABEL,
    T_MY_WEB_HOTEL_INVENTORY_DATA_APPROVE_LABEL,
    T_MY_WEB_HOTEL_INVENTORY_DATA_SAVE_LABEL,
    T_MY_WEB_HOTEL_INVENTORY_DATA_RESET_LABEL,
    T_MY_WEB_HOTEL_INVENTORY_DATA_INCORRECT_DATA,
    T_MY_WEB_HOTEL_INVENTORY_DATA_FIELD_SYSTEM_LABEL,
    T_MY_WEB_HOTEL_INVENTORY_DATA_FIELD_NAME_LABEL,
    T_MY_WEB_HOTEL_INVENTORY_DATA_FIELD_NAME_PLACEHOLDER,
    T_MY_VIRTUAL_SERVER_INVENTORY_DATA_STATE_LABEL,
    T_MY_VIRTUAL_SERVER_INVENTORY_DATA_APPROVE_LABEL,
    T_MY_VIRTUAL_SERVER_INVENTORY_DATA_SAVE_LABEL,
    T_MY_VIRTUAL_SERVER_INVENTORY_DATA_RESET_LABEL,
    T_MY_VIRTUAL_SERVER_INVENTORY_DATA_INCORRECT_DATA,
    T_MY_VIRTUAL_SERVER_INVENTORY_DATA_FIELD_SYSTEM_LABEL,
    T_MY_VIRTUAL_SERVER_INVENTORY_DATA_FIELD_NAME_LABEL,
    T_MY_VIRTUAL_SERVER_INVENTORY_DATA_FIELD_NAME_PLACEHOLDER,
    T_MY_DOMAIN_INVENTORY_DATA_STATE_LABEL,
    T_MY_DOMAIN_INVENTORY_DATA_APPROVE_LABEL,
    T_MY_DOMAIN_INVENTORY_DATA_SAVE_LABEL,
    T_MY_DOMAIN_INVENTORY_DATA_RESET_LABEL,
    T_MY_DOMAIN_INVENTORY_DATA_INCORRECT_DATA,
    T_MY_DOMAIN_INVENTORY_DATA_FIELD_SYSTEM_LABEL,
    T_MY_DOMAIN_INVENTORY_DATA_FIELD_NAME_LABEL,
    T_MY_DOMAIN_TRANSFER_INVENTORY_DATA_STATE_LABEL,
    T_MY_DOMAIN_TRANSFER_INVENTORY_DATA_APPROVE_LABEL,
    T_MY_DOMAIN_TRANSFER_INVENTORY_DATA_SAVE_LABEL,
    T_MY_DOMAIN_TRANSFER_INVENTORY_DATA_RESET_LABEL,
    T_MY_DOMAIN_TRANSFER_INVENTORY_DATA_INCORRECT_DATA,
    T_MY_DOMAIN_TRANSFER_INVENTORY_DATA_FIELD_SYSTEM_LABEL,
    T_MY_DOMAIN_TRANSFER_INVENTORY_DATA_FIELD_NAME_LABEL,
    T_MY_DOMAIN_TRANSFER_INVENTORY_DATA_FIELD_AUTH_ID_LABEL,
    T_MY_SHELL_INVENTORY_DATA_FIELD_REAL_NAME_DISCLAIMER,
} from "../../../../../../core/constants/translation";
import { InventoryData } from "../../../../../../../../io/hyperify/core/store/types/inventory/data/InventoryData";
import { ProductType } from "../../../../../../../../io/hyperify/core/store/types/product/ProductType";
import { InventoryState } from "../../../../../../../../io/hyperify/core/store/types/inventory/InventoryState";
import { PermissionList } from "../../../../../../../../io/hyperify/core/PermissionUtils";
import { SendanorApiPermission } from "../../../../../../core/types/SendanorApiPermission";
import { SelectFieldItem } from "../../../../../../../../io/hyperify/frontend/types/items/SelectFieldModel";
import { SelectFieldUtils } from "../../../../../../../../io/hyperify/frontend/utils/SelectFieldUtils";
import { InventorySubmitCallback } from "./shell/MyShellInventoryDataForm";
import "./MyInventoryData.scss";

export interface MyInventoryDataProps {
    readonly className ?: string;
    readonly t          : TranslationFunction;
    readonly type       : ProductType;
    readonly state      : InventoryState;
    readonly value      : InventoryData;
    readonly submit     ?: InventorySubmitCallback;
    readonly permissions : PermissionList<SendanorApiPermission>;
}

export function MyInventoryData (props: MyInventoryDataProps) {

    const className = props?.className;
    const t = props?.t;
    const value = props?.value;
    const type = props?.type;
    const state = props?.state;
    const submit = props?.submit;
    const permissions = props?.permissions;
    const stateValues : readonly SelectFieldItem<InventoryState>[] = SelectFieldUtils.getEnumValues<InventoryState>(
        InventoryState,
        (key: string) : string => t(`${T_INVENTORY_ITEM_STATE_PREFIX}.${key}`)
    );

    return (
        <div className={
            MY_INVENTORY_DATA_CLASS_NAME
            + (className? ` ${className}` : '')
        }>
            <MyInventoryDataForm
                type={type}
                state={state}
                stateValues={stateValues}
                value={value}
                submit={submit}
                permissions={permissions}

                shellStateLabel={t(T_MY_SHELL_INVENTORY_DATA_STATE_LABEL)}
                shellApproveLabel={t(T_MY_SHELL_INVENTORY_DATA_APPROVE_LABEL)}
                shellSaveLabel={t(T_MY_SHELL_INVENTORY_DATA_SAVE_LABEL)}
                shellResetLabel={t(T_MY_SHELL_INVENTORY_DATA_RESET_LABEL)}
                shellIncorrectDataLabel={t(T_MY_SHELL_INVENTORY_DATA_INCORRECT_DATA)}
                shellHostnameLabel={t(T_MY_SHELL_INVENTORY_DATA_FIELD_HOSTNAME_LABEL)}
                shellPortLabel={t(T_MY_SHELL_INVENTORY_DATA_FIELD_PORT_LABEL)}
                shellUsernameLabel={t(T_MY_SHELL_INVENTORY_DATA_FIELD_USERNAME_LABEL)}
                shellUsernamePlaceholder={t(T_MY_SHELL_INVENTORY_DATA_FIELD_USERNAME_PLACEHOLDER)}
                shellRealNameLabel={t(T_MY_SHELL_INVENTORY_DATA_FIELD_REAL_NAME_LABEL)}
                shellRealNameDisclaimer={t(T_MY_SHELL_INVENTORY_DATA_FIELD_REAL_NAME_DISCLAIMER)}

                emailStateLabel={t(T_MY_EMAIL_INVENTORY_DATA_STATE_LABEL)}
                emailApproveLabel={t(T_MY_EMAIL_INVENTORY_DATA_APPROVE_LABEL)}
                emailSaveLabel={t(T_MY_EMAIL_INVENTORY_DATA_SAVE_LABEL)}
                emailResetLabel={t(T_MY_EMAIL_INVENTORY_DATA_RESET_LABEL)}
                emailIncorrectDataLabel={t(T_MY_EMAIL_INVENTORY_DATA_INCORRECT_DATA)}
                emailHostnameLabel={t(T_MY_EMAIL_INVENTORY_DATA_FIELD_HOSTNAME_LABEL)}
                emailUsernameLabel={t(T_MY_EMAIL_INVENTORY_DATA_FIELD_USERNAME_LABEL)}
                emailUsernamePlaceholder={t(T_MY_EMAIL_INVENTORY_DATA_FIELD_USERNAME_PLACEHOLDER)}

                virtualServerStateLabel={t(T_MY_VIRTUAL_SERVER_INVENTORY_DATA_STATE_LABEL)}
                virtualServerApproveLabel={t(T_MY_VIRTUAL_SERVER_INVENTORY_DATA_APPROVE_LABEL)}
                virtualServerSaveLabel={t(T_MY_VIRTUAL_SERVER_INVENTORY_DATA_SAVE_LABEL)}
                virtualServerResetLabel={t(T_MY_VIRTUAL_SERVER_INVENTORY_DATA_RESET_LABEL)}
                virtualServerIncorrectDataLabel={t(T_MY_VIRTUAL_SERVER_INVENTORY_DATA_INCORRECT_DATA)}
                virtualServerSystemLabel={t(T_MY_VIRTUAL_SERVER_INVENTORY_DATA_FIELD_SYSTEM_LABEL)}
                virtualServerNameLabel={t(T_MY_VIRTUAL_SERVER_INVENTORY_DATA_FIELD_NAME_LABEL)}
                virtualServerNamePlaceholder={t(T_MY_VIRTUAL_SERVER_INVENTORY_DATA_FIELD_NAME_PLACEHOLDER)}

                webHotelStateLabel={t(T_MY_WEB_HOTEL_INVENTORY_DATA_STATE_LABEL)}
                webHotelApproveLabel={t(T_MY_WEB_HOTEL_INVENTORY_DATA_APPROVE_LABEL)}
                webHotelSaveLabel={t(T_MY_WEB_HOTEL_INVENTORY_DATA_SAVE_LABEL)}
                webHotelResetLabel={t(T_MY_WEB_HOTEL_INVENTORY_DATA_RESET_LABEL)}
                webHotelIncorrectDataLabel={t(T_MY_WEB_HOTEL_INVENTORY_DATA_INCORRECT_DATA)}
                webHotelSystemLabel={t(T_MY_WEB_HOTEL_INVENTORY_DATA_FIELD_SYSTEM_LABEL)}
                webHotelNameLabel={t(T_MY_WEB_HOTEL_INVENTORY_DATA_FIELD_NAME_LABEL)}
                webHotelNamePlaceholder={t(T_MY_WEB_HOTEL_INVENTORY_DATA_FIELD_NAME_PLACEHOLDER)}

                domainStateLabel={t(T_MY_DOMAIN_INVENTORY_DATA_STATE_LABEL)}
                domainApproveLabel={t(T_MY_DOMAIN_INVENTORY_DATA_APPROVE_LABEL)}
                domainSaveLabel={t(T_MY_DOMAIN_INVENTORY_DATA_SAVE_LABEL)}
                domainResetLabel={t(T_MY_DOMAIN_INVENTORY_DATA_RESET_LABEL)}
                domainIncorrectDataLabel={t(T_MY_DOMAIN_INVENTORY_DATA_INCORRECT_DATA)}
                domainSystemLabel={t(T_MY_DOMAIN_INVENTORY_DATA_FIELD_SYSTEM_LABEL)}
                domainNameLabel={t(T_MY_DOMAIN_INVENTORY_DATA_FIELD_NAME_LABEL)}

                domainTransferStateLabel={t(T_MY_DOMAIN_TRANSFER_INVENTORY_DATA_STATE_LABEL)}
                domainTransferApproveLabel={t(T_MY_DOMAIN_TRANSFER_INVENTORY_DATA_APPROVE_LABEL)}
                domainTransferSaveLabel={t(T_MY_DOMAIN_TRANSFER_INVENTORY_DATA_SAVE_LABEL)}
                domainTransferResetLabel={t(T_MY_DOMAIN_TRANSFER_INVENTORY_DATA_RESET_LABEL)}
                domainTransferIncorrectDataLabel={t(T_MY_DOMAIN_TRANSFER_INVENTORY_DATA_INCORRECT_DATA)}
                domainTransferSystemLabel={t(T_MY_DOMAIN_TRANSFER_INVENTORY_DATA_FIELD_SYSTEM_LABEL)}
                domainTransferNameLabel={t(T_MY_DOMAIN_TRANSFER_INVENTORY_DATA_FIELD_NAME_LABEL)}
                domainTransferAuthIdLabel={t(T_MY_DOMAIN_TRANSFER_INVENTORY_DATA_FIELD_AUTH_ID_LABEL)}

            />
        </div>
    );
}

